import {Component, ViewChild} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';

import {BoiteDialogueGeneriqueComponent, DonneesTreeViewItem, SimpleImageComponent, SimpleLibelleComponent, SimpleTreeViewComponent} from '../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import {AeFTransfererDocumentFactureAchatDetail} from '../../../../../../val-ammon-services/val-services-efact-lib/src/public-api';

//Clés de traduction des libellés utilisateur des codes cas métier
//  Les cas relayés du back-office (SU #116134) cohabitent avec ceux produits côté SAS eFacture par
//  les deux traitements issus du découpage du transfert (Fonctionnalité #116844).
const LIBELLES_CODE_CAS: {[code: string]: string} =
{
  "Succes"                              : "achat.logs-transfert.code-cas.succes",
  "CommandeNonTrouvee"                  : "achat.logs-transfert.code-cas.commande-non-trouvee",
  "ComplementComptableNonTrouve"        : "achat.logs-transfert.code-cas.complement-comptable-non-trouve",
  "IbanTransmisSansComplementComptable" : "achat.logs-transfert.code-cas.iban-transmis-sans-complement-comptable",
  "MontantRestantInsuffisant"           : "achat.logs-transfert.code-cas.montant-restant-insuffisant",
  "AucunDocumentATransferer"            : "achat.logs-transfert.code-cas.aucun-document-a-transferer",
  "DetailDocumentsAbsent"               : "achat.logs-transfert.code-cas.detail-documents-absent",
  "AvoirNonTransmissible"               : "achat.logs-transfert.code-cas.avoir-non-transmissible",
  "TopPieceAchatNonEcrit"               : "achat.logs-transfert.code-cas.top-piece-achat-non-ecrit",
  "ErreurTechnique"                     : "achat.logs-transfert.code-cas.erreur-technique"
};

//Détail commun aux deux traitements de transfert vers le back-office
//  Les résultats de « Transférer les documents » (SU #116854) et de « Créer la pièce d’achat »
//  (SU #116856) partagent le socle relayé du back-office et n’ajoutent chacun que ses propres
//  champs : la fenêtre de logs les restitue tous les deux sans se dédoubler.
export interface DetailTransfertFactureAchat
{
  //Identifiant interne (BD Ammon) de la facture d’achat
  identifiantFacture: string;

  //Référence de la facture d’achat (BT-1)
  referenceFacture: string;

  //Statut global : « Succes » ou « Erreur »
  statut: string;

  //Code cas métier détaillé
  codeCas?: string;

  //Message applicatif
  message?: string;

  //Valeurs d’entrée utiles au diagnostic selon le cas
  valeursEntree?: {[cle: string]: unknown};

  //Détail de l’erreur technique
  detailErreur?: string;

  //Résultat du dépôt document par document — « Transférer les documents » uniquement
  documents?: AeFTransfererDocumentFactureAchatDetail[];

  //Référence de la pièce d’achat créée — « Créer la pièce d’achat » uniquement
  referencePieceAchat?: string;

  //Identifiant du dossier ciblé — « Créer la pièce d’achat » uniquement
  identifiantDossier?: string;
}

//Composant fenêtre de logs des traitements de transfert des factures d’achat
@Component (
{
  selector: 'val-logs-transfert-factures-achat',
  imports:
  [
    BoiteDialogueGeneriqueComponent,
    SimpleTreeViewComponent,
    SimpleLibelleComponent,
    SimpleImageComponent,
    TranslocoPipe
  ],
  templateUrl: './logs-transfert-factures-achat.component.html',
  styleUrl: './logs-transfert-factures-achat.component.scss'
})
export class LogsTransfertFacturesAchatComponent
{
  //Création
  //  Paramètres
  //    translocoService : Service de traduction
  constructor (private translocoService: TranslocoService)
  {
    this.titre = "";
    this.bandeau = "";
    this.items = [];
    this.itemsOuverts = [];
    this.nombreErreur = 0;
    this.nombreSucces = 0;
  }

  //Boîte de dialogue log
  @ViewChild ("boiteDialogueLog")
  public boiteDialogueLog!: BoiteDialogueGeneriqueComponent<void>;

  //Titre de la boîte de dialogue
  public titre: string;

  //Bandeau
  public bandeau: string;

  //Items du tree view
  public items: DonneesTreeViewItem[];

  //Identifiants des items ouverts
  public itemsOuverts: number[];

  //Nombre d’erreur
  public nombreErreur: number;

  //Nombre de succès
  public nombreSucces: number;

  //Identifiant en cours pour la construction du tree view
  private identifiantEnCours: number = 0;

  //Ouvre la fenêtre de logs
  //  Paramètres
  //    titre   : Titre de la fenêtre — nomme le traitement dont on lit les logs
  //    bandeau : Texte du bandeau
  //    details : Résultat détaillé par facture
  public async Ouvrir (titre: string, bandeau: string, details: DetailTransfertFactureAchat[]) : Promise<void>
  {
    this.titre = titre;
    this.bandeau = bandeau;
    this.identifiantEnCours = 0;
    this.items = [];
    this.itemsOuverts = [];
    this.nombreErreur = 0;
    this.nombreSucces = 0;

    for (const detail of details)
      this.AjouterDetail (detail);

    await this.boiteDialogueLog.Ouvrir ();
  }

  //Ferme la fenêtre de logs
  public Fermer () : void
  {
    this.boiteDialogueLog.fonctionRetour ();
    this.boiteDialogueLog.Fermer ();
  }

  //Ajoute un détail au tree view
  //  Paramètres
  //    detail : Détail du traitement d’une facture
  private AjouterDetail (detail: DetailTransfertFactureAchat) : void
  {
    let item: DonneesTreeViewItem;
    let succes: boolean;

    succes = detail.statut === "Succes";

    if (succes)
      this.nombreSucces++;
    else
      this.nombreErreur++;

    this.identifiantEnCours++;

    item = new DonneesTreeViewItem ();
    item.identifiant = this.identifiantEnCours;
    item.niveau = succes ? 3 : 0;
    item.texte = this.FormaterTexteFacture (detail);
    item.items = [];

    this.AjouterSousItemsValeursEntree (item, detail);
    this.AjouterSousItemResultat (item, detail);
    this.AjouterSousItemsDocuments (item, detail);
    this.AjouterSousItemDetailErreur (item, detail);

    if (item.items.length === 0)
      item.items = undefined;
    else
      this.itemsOuverts.push (item.identifiant);

    this.items = this.items.concat ([item]);
  }

  //Formate le texte du niveau facture (référence — libellé du cas)
  //  Paramètres
  //    detail : Détail du traitement d’une facture
  //  Retourne
  //    Texte à afficher
  private FormaterTexteFacture (detail: DetailTransfertFactureAchat) : string
  {
    let libelle: string;
    let reference: string;

    libelle = this.TraduireCodeCas (detail.codeCas, detail.message);
    reference = detail.referenceFacture && detail.referenceFacture.length > 0
      ? detail.referenceFacture
      : detail.identifiantFacture;

    return `${reference} — ${libelle}`;
  }

  //Traduit un code cas en libellé utilisateur
  //  Paramètres
  //    codeCas : Code cas du traitement
  //    message : Message applicatif de secours
  //  Retourne
  //    Libellé utilisateur
  private TraduireCodeCas (codeCas: string | undefined, message: string | undefined) : string
  {
    let libelle: string | undefined;

    if (codeCas && LIBELLES_CODE_CAS[codeCas])
      libelle = this.translocoService.translate (LIBELLES_CODE_CAS[codeCas]);

    if (!libelle)
      libelle = message && message.length > 0 ? message : this.translocoService.translate ("achat.logs-transfert.motif-inconnu");

    //translate est typé any : le rétrécissement de libelle ne survit pas à l'affectation
    return libelle ?? "";
  }

  //Ajoute les valeurs d’entrée en sous-items
  //  Paramètres
  //    item   : Item parent
  //    detail : Détail du traitement d’une facture
  private AjouterSousItemsValeursEntree (item: DonneesTreeViewItem, detail: DetailTransfertFactureAchat) : void
  {
    if (!detail.valeursEntree)
      return;

    for (const cle of Object.keys (detail.valeursEntree))
    {
      const valeur = detail.valeursEntree[cle];

      this.AjouterSousItem (item, this.translocoService.translate ("achat.logs-transfert.valeur-entree", {cle: cle, valeur: this.FormaterValeur (valeur)}), item.niveau);
    }
  }

  //Ajoute le résultat de la création de la pièce d’achat en sous-item
  //  Paramètres
  //    item   : Item parent
  //    detail : Détail du traitement d’une facture
  private AjouterSousItemResultat (item: DonneesTreeViewItem, detail: DetailTransfertFactureAchat) : void
  {
    let dossier: string;
    let pieceAchat: string;

    if (detail.statut !== "Succes" || (!detail.referencePieceAchat && !detail.identifiantDossier))
      return;

    dossier = detail.identifiantDossier ?? "";
    pieceAchat = detail.referencePieceAchat ?? "";

    this.AjouterSousItem (item, this.translocoService.translate ("achat.logs-transfert.dossier-piece-achat", {dossier: dossier, pieceAchat: pieceAchat}), 3);
  }

  //Ajoute le résultat du dépôt document par document en sous-items
  //  Le détail par document est la raison d’être des logs du traitement « Transférer les
  //  documents » : un dépôt partiel réussit sur certains documents et échoue sur d’autres, et
  //  seul ce niveau permet de savoir lesquels reprendre.
  //  Paramètres
  //    item   : Item parent
  //    detail : Détail du traitement d’une facture
  private AjouterSousItemsDocuments (item: DonneesTreeViewItem, detail: DetailTransfertFactureAchat) : void
  {
    if (!detail.documents || detail.documents.length === 0)
      return;

    for (const detailDocument of detail.documents)
    {
      if (detailDocument.reussi)
        this.AjouterSousItem (item, this.translocoService.translate ("achat.logs-transfert.document-depose", {nom: detailDocument.nom}), 3);
      else
        this.AjouterSousItem (item, this.translocoService.translate ("achat.logs-transfert.document-echec",
                                                                     {nom: detailDocument.nom,
                                                                      message: detailDocument.message && detailDocument.message.length > 0 ? detailDocument.message : this.translocoService.translate ("achat.logs-transfert.echec-depot")}), 0);
    }
  }

  //Ajoute le détail d’une erreur technique en sous-item
  //  Paramètres
  //    item   : Item parent
  //    detail : Détail du traitement d’une facture
  private AjouterSousItemDetailErreur (item: DonneesTreeViewItem, detail: DetailTransfertFactureAchat) : void
  {
    if (detail.codeCas !== "ErreurTechnique" || !detail.detailErreur)
      return;

    this.AjouterSousItem (item, this.translocoService.translate ("achat.logs-transfert.detail-erreur", {detail: detail.detailErreur}), 0);
  }

  //Ajoute un sous-item générique
  //  Paramètres
  //    parent : Item parent
  //    texte  : Texte du sous-item
  //    niveau : Niveau (icône) du sous-item
  private AjouterSousItem (parent: DonneesTreeViewItem, texte: string, niveau: number) : void
  {
    let sousItem: DonneesTreeViewItem;

    this.identifiantEnCours++;

    sousItem = new DonneesTreeViewItem ();
    sousItem.identifiant = this.identifiantEnCours;
    sousItem.niveau = niveau;
    sousItem.texte = texte;

    parent.items!.push (sousItem);
  }

  //Formate une valeur d’entrée
  //  Paramètres
  //    valeur : Valeur à formater
  //  Retourne
  //    Valeur formatée pour affichage
  private FormaterValeur (valeur: unknown) : string
  {
    let formatee: string;

    if (valeur === null || valeur === undefined)
      formatee = "";
    else if (typeof valeur === "string" || typeof valeur === "number" || typeof valeur === "boolean")
      formatee = String (valeur);
    else
      formatee = JSON.stringify (valeur);

    return formatee;
  }
}
